import React, { useState, useEffect, useRef } from "react";
import { Modal, Input, Select, Checkbox, message } from "antd";
import { debounce } from "lodash";
import httpBase from "../../../../utils/http.utils";

const { Option } = Select;

const ADDITIONAL_INFO_STORAGE_KEYS = {
    phoneNumber: "playgroundPhoneNumber",
    primaryProduct: "playgroundPrimaryProduct",
    primaryProductLabel: "playgroundPrimaryProductLabel",
};

const setStoredValue = (key, value) => {
    if (typeof window === "undefined") {
        return;
    }

    if (value) {
        localStorage.setItem(key, value);
        return;
    }

    localStorage.removeItem(key);
};

const fieldContainerStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "14px",
    backgroundColor: "#fbfcff",
    border: "1px solid #eef2f7",
    borderRadius: "8px",
};

const fieldLabelStyle = {
    fontSize: "13px",
    color: "#1f2937",
    fontWeight: 600,
};

const AdditionalInfoModal = ({
    modalVisible,
    setModalVisible,
    phoneNumber,
    setPhoneNumber,
    primaryProduct,
    setPrimaryProduct,
    primaryProductLabel,
    setPrimaryProductLabel,
    automaticMessage,
    setAutomaticMessage,
    setSimulateAdClick,
}) => {
    const [productOptions, setProductOptions] = useState([]);
    const [productSearching, setProductSearching] = useState(false);
    const [localAutoMessageEnabled, setLocalAutoMessageEnabled] = useState(false);
    const [localPhone, setLocalPhone] = useState("");
    const [localProduct, setLocalProduct] = useState("");
    const [localProductLabel, setLocalProductLabel] = useState("");
    const [localAutoMessage, setLocalAutoMessage] = useState("");

    const fetchProductsRef = useRef(null);

    useEffect(() => {
        if (modalVisible) {
            setLocalAutoMessageEnabled(!!automaticMessage);
            setLocalPhone(phoneNumber || "");
            setLocalProduct(primaryProduct || "");
            setLocalProductLabel(primaryProductLabel || "");
            setLocalAutoMessage(automaticMessage || "");
        }
    }, [modalVisible, phoneNumber, primaryProduct, primaryProductLabel, automaticMessage]);

    useEffect(() => {
        fetchProductsRef.current = debounce((search = "") => {
            setProductSearching(true);
            httpBase().get("/product/products", {
                params: { skip: 0, limit: 10, Search: search },
            })
                .then((res) => {
                    const products = res?.data?.products || [];
                    const options = products
                        .filter((p) => p && p._id && p.name)
                        .map((p) => ({
                            label: p.name,
                            value: p._id,
                            code: p.code,
                        }));
                    setProductOptions(options);
                })
                .catch(() => setProductOptions([]))
                .finally(() => setProductSearching(false));
        }, 500);

        return () => {
            if (fetchProductsRef.current) {
                fetchProductsRef.current.cancel();
            }
        };
    }, []);

    const handleProductSearch = (value) => {
        if (fetchProductsRef.current) {
            fetchProductsRef.current(value);
        }
    };

    const handleProductChange = (value, option) => {
        setLocalProduct(value || "");
        setLocalProductLabel(option?.label || "");
    };

    const handleSave = () => {
        const nextPhone = localPhone || "";
        const nextProduct = localProduct || "";
        const nextProductLabel = localProductLabel || "";

        setPhoneNumber(nextPhone);
        setPrimaryProduct(nextProduct);
        setPrimaryProductLabel(nextProductLabel);
        setStoredValue(ADDITIONAL_INFO_STORAGE_KEYS.phoneNumber, nextPhone);
        setStoredValue(ADDITIONAL_INFO_STORAGE_KEYS.primaryProduct, nextProduct);
        setStoredValue(ADDITIONAL_INFO_STORAGE_KEYS.primaryProductLabel, nextProductLabel);
        if (localAutoMessageEnabled) {
            setAutomaticMessage(localAutoMessage || "");
        } else {
            setAutomaticMessage("");
        }
        message.success("Additional info saved!");
        setModalVisible(false);
        if (
            typeof setSimulateAdClick === "function" &&
            (nextPhone || nextProduct || (localAutoMessageEnabled && localAutoMessage))
        ) {
            setSimulateAdClick(true);
        }
    };

    const handleClear = () => {
        setLocalAutoMessageEnabled(false);
        setLocalPhone("");
        setLocalProduct("");
        setLocalProductLabel("");
        setPrimaryProductLabel("");
        setLocalAutoMessage("");
        setPhoneNumber("");
        setPrimaryProduct("");
        setAutomaticMessage("");
        if (typeof setSimulateAdClick === "function") {
            setSimulateAdClick(false);
        }
        if (typeof window !== "undefined") {
            Object.values(ADDITIONAL_INFO_STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
        }
        message.info("Fields cleared");
        setModalVisible(false);
    };

    const handleCancel = () => {
        setModalVisible(false);
    };

    const selectedProductOptions = productOptions.some((opt) => opt.value === localProduct) || !localProduct
        ? productOptions
        : [{ label: localProductLabel || localProduct, value: localProduct }, ...productOptions];

    return (
        <Modal
            title={
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div
                        style={{
                            width: "34px",
                            height: "34px",
                            borderRadius: "8px",
                            backgroundColor: "#e6f4ff",
                            border: "1px solid #bae0ff",
                            color: "#1677ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <iconify-icon icon="mdi:account-details-outline" width="20" height="20" />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <span style={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>
                            Additional Customer Information
                        </span>
                        <span style={{ fontSize: "12px", fontWeight: 400, color: "#6b7280" }}>
                            Configure optional ad-click context for the next reply.
                        </span>
                    </div>
                </div>
            }
            open={modalVisible}
            onCancel={handleCancel}
            onOk={handleSave}
            okText="Save"
            cancelText="Cancel"
            width={560}
            styles={{
                content: { borderRadius: "12px", overflow: "hidden" },
                header: { paddingBottom: "14px", borderBottom: "1px solid #f0f0f0" },
                body: { paddingTop: "18px" },
            }}
            footer={(_, { OkBtn, CancelBtn }) => (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <div
                        style={{
                            cursor: "pointer",
                            color: "#ff4d4f",
                            fontSize: "13px",
                            fontWeight: 500,
                            padding: "4px 0",
                        }}
                        onClick={handleClear}
                    >
                        Clear all fields
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <CancelBtn />
                        <OkBtn />
                    </div>
                </div>
            )}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div style={fieldContainerStyle}>
                    <div style={fieldLabelStyle}>Phone Number</div>
                    <Input
                        value={localPhone}
                        onChange={(e) => setLocalPhone(e.target.value)}
                        placeholder="e.g. +977-9841234567"
                        prefix={
                            <iconify-icon
                                icon="mdi:phone-outline"
                                width="16"
                                height="16"
                                style={{ color: "#bfbfbf" }}
                            />
                        }
                        maxLength={20}
                        allowClear
                        size="large"
                        style={{ borderRadius: "8px" }}
                    />
                </div>

                <div style={fieldContainerStyle}>
                    <div style={fieldLabelStyle}>Primary Product</div>
                    <Select
                        value={localProduct || undefined}
                        placeholder="Search and select a product"
                        allowClear
                        showSearch
                        onSearch={handleProductSearch}
                        notFoundContent={productSearching ? "Searching..." : "No products found"}
                        optionLabelProp="label"
                        filterOption={false}
                        onChange={handleProductChange}
                        size="large"
                        style={{ width: "100%" }}
                    >
                        {selectedProductOptions.map((opt) => (
                            <Option key={opt.value} value={opt.value} label={opt.label} code={opt.code}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span>{opt.label}</span>
                                    {opt.code && (
                                        <span style={{ color: "#8c8c8c", fontSize: "12px", marginLeft: "8px" }}>
                                            {opt.code}
                                        </span>
                                    )}
                                </div>
                            </Option>
                        ))}
                    </Select>
                </div>

                <div
                    style={{
                        padding: "12px 14px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #eef2f7",
                        borderRadius: "8px",
                    }}
                >
                    <Checkbox
                        checked={localAutoMessageEnabled}
                        onChange={(e) => setLocalAutoMessageEnabled(e.target.checked)}
                    >
                        <span style={{ fontWeight: 600, color: "#1f2937" }}>
                            Include automatic message with ad click
                        </span>
                    </Checkbox>
                </div>

                <div style={fieldContainerStyle}>
                    <div style={fieldLabelStyle}>
                        {localAutoMessageEnabled ? "Edit automatic message" : "Automatic Message after Ad Click"}
                    </div>
                    {localAutoMessageEnabled ? (
                        <Input.TextArea
                            value={localAutoMessage}
                            onChange={(e) => setLocalAutoMessage(e.target.value)}
                            placeholder="e.g. Hello, are you interested in joining our new physical and online preparation class?"
                            maxLength={500}
                            autoSize={{ minRows: 2, maxRows: 4 }}
                            style={{ borderRadius: "8px" }}
                        />
                    ) : (
                        <div
                            style={{
                                padding: "10px 12px",
                                backgroundColor: "#f5f7fa",
                                borderRadius: "8px",
                                color: "#8c8c8c",
                                fontSize: "13px",
                                border: "1px solid #e5e7eb",
                                minHeight: "40px",
                            }}
                        >
                            {localAutoMessage || "No automatic message set"}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default AdditionalInfoModal;
